/**
 * HeyScent Pixel Tracking Bridge
 * Browser pixel + Server-side CAPI (Meta + TikTok)
 * Deduplication via event_id sent to both browser pixel and CAPI.
 * Nothing is sent unless the visitor accepted marketing cookies
 * (window.hsConsent, set up in layout/theme.liquid).
 */
(function () {
  'use strict';

  var CAPI_URL = 'https://api.heyscent.co/capi.php';

  // --- Helpers ---
  function genEventId() {
    return 'hs_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  // --- Consent ---
  function allowed() {
    return !!(window.hsConsent && window.hsConsent.marketingAllowed());
  }

  function whenAllowed(cb) {
    if (window.hsConsent) window.hsConsent.onMarketingAllowed(cb);
  }

  // Store the visitor's choice on the cart, so purchase-webhook.php only
  // sends orders from visitors who said yes.
  function syncConsentAttribute() {
    var token = getCookie('cart');
    if (!token) return;
    var value = allowed() ? 'yes' : 'no';
    var marker = token + '|' + value;
    try { if (sessionStorage.getItem('hs_consent_attr') === marker) return; } catch (e) {}
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _hs_marketing_consent: value } }),
      keepalive: true,
    })
      .then(function (r) {
        if (r.ok) { try { sessionStorage.setItem('hs_consent_attr', marker); } catch (e) {} }
      })
      .catch(function () {});
  }

  document.addEventListener('visitorConsentCollected', syncConsentAttribute);
  whenAllowed(syncConsentAttribute);

  function getUserData() {
    var data = {};
    // Get customer data if injected by Liquid
    var cd = window.__hsCustomerData;
    if (cd) {
      if (cd.email) data.em = cd.email;
      if (cd.phone) data.ph = cd.phone;
      if (cd.fn) data.fn = cd.fn;
      if (cd.ln) data.ln = cd.ln;
    }
    // Meta click ID and browser ID cookies
    data.fbc = getCookie('_fbc') || null;
    data.fbp = getCookie('_fbp') || null;
    return data;
  }

  function sendCAPI(eventName, customData) {
    if (!allowed()) return null;
    var eventId = genEventId();
    var userData = getUserData();

    // 1. Fire browser pixel with event_id for dedup
    if (typeof fbq === 'function') {
      var fbParams = Object.assign({}, customData || {});
      fbq('track', eventName, fbParams, { eventID: eventId });
    }
    if (typeof ttq !== 'undefined') {
      var ttParams = {};
      if (customData) {
        if (customData.content_ids) {
          ttParams.contents = customData.content_ids.map(function (id) {
            var item = { content_id: String(id), content_type: 'product' };
            if (customData.content_name) item.content_name = customData.content_name;
            return item;
          });
        }
        if (customData.value) ttParams.value = customData.value;
        if (customData.currency) ttParams.currency = customData.currency;
        if (customData.content_name) ttParams.description = customData.content_name;
        ttParams.content_type = 'product';
      }
      ttq.track(eventName === 'Purchase' ? 'CompletePayment' : eventName, ttParams);
    }

    // 2. Send server-side CAPI (non-blocking)
    try {
      var payload = {
        event_name: eventName,
        event_id: eventId,
        source_url: window.location.href,
        user_data: userData,
        custom_data: customData || {},
      };

      if (navigator.sendBeacon) {
        navigator.sendBeacon(CAPI_URL, JSON.stringify(payload));
      } else {
        fetch(CAPI_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(function () {});
      }
    } catch (e) {
      // CAPI failure should never block the user
    }

    return eventId;
  }

  // --- PageView: send to CAPI (browser pixel fires in theme.liquid) ---
  whenAllowed(function () {
    var eventId = window.__hsPageViewId || genEventId();
    var userData = getUserData();
    try {
      var payload = {
        event_name: 'PageView',
        event_id: eventId,
        source_url: window.location.href,
        user_data: userData,
        custom_data: {},
      };
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CAPI_URL, JSON.stringify(payload));
      } else {
        fetch(CAPI_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(function () {});
      }
    } catch (e) {}
  });

  // --- ViewContent: fires on product pages ---
  var productData = window.__hsProductData;
  if (productData) {
    whenAllowed(function () {
      sendCAPI('ViewContent', {
        content_ids: [String(productData.variant_id)],
        content_type: 'product',
        content_name: productData.title,
        content_category: productData.type,
        value: productData.price,
        currency: 'SEK',
      });
    });
  }

  // --- AddToCart: listen to theme cart:update events ---
  document.addEventListener('cart:update', function (e) {
    syncConsentAttribute();
    var detail = e.detail || {};
    var data = detail.data || {};

    if (data.didError) return;
    if (data.source !== 'product-form-component' && data.source !== 'quick-add' && data.source !== 'upsell-modal' && data.source !== 'bundle-builder') return;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) return;
        var item = cart.items[cart.items.length - 1];
        var value = item.final_line_price / 100;

        sendCAPI('AddToCart', {
          content_ids: [String(item.variant_id)],
          content_type: 'product',
          content_name: item.product_title,
          value: value,
          currency: 'SEK',
        });
      })
      .catch(function () {
        sendCAPI('AddToCart', {});
      });
  });

  // --- InitiateCheckout: track without blocking other handlers (e.g. gift system) ---
  var checkoutFired = false;

  function trackCheckout() {
    syncConsentAttribute();
    if (checkoutFired) return;
    checkoutFired = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var ids = cart.items.map(function (item) { return String(item.variant_id); });
        var value = cart.total_price / 100;

        sendCAPI('InitiateCheckout', {
          content_ids: ids,
          content_type: 'product',
          value: value,
          currency: 'SEK',
          num_items: cart.item_count,
        });
      })
      .catch(function () {
        sendCAPI('InitiateCheckout', {});
      });
  }

  function bindCheckoutButtons() {
    var btns = document.querySelectorAll('[name="checkout"], a[href="/checkout"], a[href*="/checkout"]');
    btns.forEach(function (btn) {
      if (btn.dataset.pixelBound) return;
      if (btn.closest('.shopify-payment-button')) return;
      btn.dataset.pixelBound = 'true';
      // Use bubble phase, don't preventDefault/stopPropagation — let gift handler and form work normally
      btn.addEventListener('click', function (e) {
        if (e.target.closest('.shopify-payment-button')) return;
        trackCheckout();
      });
    });
  }

  bindCheckoutButtons();
  var observer = new MutationObserver(function () {
    bindCheckoutButtons();
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
